import { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { Modal, Button, Spinner, FitThumb, inputClass } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { cn } from '@/lib/utils';
import type { Fit } from '@/lib/types';

interface SelectFitModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Assigns the chosen fit to the slot being filled. */
  onSelect: (fitId: string) => Promise<void>;
}

export function SelectFitModal({ isOpen, onClose, onSelect }: SelectFitModalProps) {
  const { data, loading } = useApi<{ fits: Fit[] }>(isOpen ? '/fits' : null);
  const [query, setQuery] = useState('');
  const [selecting, setSelecting] = useState<string | null>(null);

  const fits = data?.fits ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return fits;
    return fits.filter((f) => f.name.toLowerCase().includes(q));
  }, [fits, query]);

  const handleSelect = async (fitId: string) => {
    setSelecting(fitId);
    try {
      await onSelect(fitId);
      onClose();
    } finally {
      setSelecting(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choose Fit">
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search fits…"
            className={cn(inputClass, 'pl-10')}
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner className="w-6 h-6" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No fits found.</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((fit) => (
              <button
                key={fit.id}
                type="button"
                onClick={() => handleSelect(fit.id)}
                disabled={selecting !== null}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/30 transition-all text-left disabled:opacity-50"
              >
                <FitThumb url={fit.imageUrl} piecePhotoUrls={fit.piecePhotoUrls} alt={fit.name} className="w-12 h-12 rounded-lg flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{fit.name}</p>
                  {fit.pieceCount != null && (
                    <p className="text-xs text-muted-foreground">{fit.pieceCount} pieces</p>
                  )}
                </div>
                {selecting === fit.id ? (
                  <Spinner className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <Check className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="pt-4">
        <Button variant="outline" fullWidth onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
